import { useCallback, useEffect, useState } from 'react'
import LeaveRequestSourceEnumHelper from '../../../../database/enums/helpers/leave-request-source.helper'
import { LeaveRequestSource } from '../../../../database/enums/LeaveRequestSource.enum'
import adminLeaveRequestsUnrespondedPageStore from '../../../../store/adminLeaveRequestsUnrespondedPageStore'
import { FetchStatus } from '../../../../types/fetch-status.types'
import constants from '../../../../constants'
import LeaveRequestService from '../../../../services/leave-request'
import { LeaveRequest } from '../../../../database/tables'
import { GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid'
import InfoIcon from '@mui/icons-material/Info'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { LeaveTime } from '../../../../database/enums/LeaveTime.enum'
import LeaveTimeEnumHelper from '../../../../database/enums/helpers/leave-time.helper'
import { LeaveType } from '../../../../database/enums/LeaveType.enum'
import LeaveTypeEnumHelper from '../../../../database/enums/helpers/leave-type.helper '
import { AccountTeam } from '../../../../database/enums/AccountTeam.enum'
import AccountTeamEnumHelper from '../../../../database/enums/helpers/account-team.helper'
import { useNavigate } from 'react-router'
import links from '../../../../routes/links'
import adminLeaveRequestApprovalPopupStore from '../../../../store/adminLeaveRequestApprovalPopupStore'
import { ApprovalStatus } from '../../../../database/enums/ApprovalStatus.enum'
import authStore from '../../../../store/authStore'
import PERMISSIONS from '../../../../private/permissions'
import toast from 'react-hot-toast'
import IconEdit from '@mui/icons-material/Edit'

const { defaultActionWidth, defaultBaseWidth, defaultDateTimeWidth, defaultDateWidth } = constants.dataGrid
export const scopeNamePrefix = 'admin-leave-requests-unresponded'

export default function useAdminLeaveRequestsUnrespondedPageViewModel() {
  const navigate = useNavigate()
  const user = authStore(state => state.user)

  const [dataFetchStatus, setDataFetchStatus] = useState<FetchStatus>(constants.fetchStatus.default)
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([])
  const selectedSourceTab = adminLeaveRequestsUnrespondedPageStore(state => state.selectedSourceTab)

  const sourceTabs = Object.values(LeaveRequestSource).map(value => ({
    value,
    label: LeaveRequestSourceEnumHelper.getLabel(value),
  }))

  const canApproval = useCallback(() => {
    if (!!user && !!user.email) return PERMISSIONS.getApprovalEmails().includes(user.email)
    return false
  }, [user])()

  const tableColumns: GridColDef<LeaveRequest>[] = [
    { field: 'name', headerName: 'Name', editable: true, minWidth: defaultBaseWidth, type: 'string' },
    {
      field: 'accountTeam',
      headerName: 'Team',
      minWidth: defaultBaseWidth,
      type: 'singleSelect',
      valueOptions: Object.values(AccountTeam),
      getOptionLabel: value => AccountTeamEnumHelper.getLabel(value as AccountTeam),
    },
    { field: 'startDate', headerName: 'Start Date', editable: true, minWidth: defaultDateWidth, type: 'date' },
    { field: 'endDate', headerName: 'End Date', editable: true, minWidth: defaultDateWidth, type: 'date' },
    {
      field: 'leaveTime',
      headerName: 'Time',
      minWidth: defaultDateWidth,
      type: 'singleSelect',
      valueOptions: Object.values(LeaveTime),
      getOptionLabel: value => LeaveTimeEnumHelper.getLabel(value as LeaveTime),
    },
    {
      field: 'leaveType',
      headerName: 'Type',
      flex: 1,
      minWidth: defaultBaseWidth,
      type: 'singleSelect',
      valueOptions: Object.values(LeaveType),
      getOptionLabel: value => LeaveTypeEnumHelper.getLabel(value as LeaveType),
    },
    { field: 'createdAt', headerName: 'Created At', editable: true, minWidth: defaultDateTimeWidth, type: 'dateTime' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: defaultActionWidth,
      type: 'actions',
      getActions: (params: GridRowParams<LeaveRequest>) => {
        const isResponded = params.row.approvalStatus !== ApprovalStatus.UNRESPONDED
        return [
          <GridActionsCellItem
            key={`${scopeNamePrefix}-table-actions-${params.id}-info`}
            icon={<InfoIcon color='info' />}
            label='Info'
            onClick={() => navigate(links.admin.leaveRequestDetail({ id: params.row.id }))}
            showInMenu
          />,
          <GridActionsCellItem
            key={`${scopeNamePrefix}-table-actions-${params.id}-edit`}
            icon={<IconEdit color='warning' />}
            label='Edit'
            onClick={() => onEditClick(isResponded, params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            key={`${scopeNamePrefix}-table-actions-${params.id}-approve`}
            icon={<CheckCircleIcon color={canApproval ? 'success' : 'disabled'} />}
            label='Approve'
            onClick={() => onApproveClick(params.row)}
          />,
          <GridActionsCellItem
            key={`${scopeNamePrefix}-table-actions-${params.id}-reject`}
            icon={<CancelIcon color={canApproval ? 'error' : 'disabled'} />}
            label='Reject'
            onClick={() => onRejectClick(params.row)}
          />,
        ]
      },
    },
  ]

  async function fetchLeaveRequestsUnrespondedData() {
    setDataFetchStatus({ ...constants.fetchStatus.default, isLoading: true })
    await LeaveRequestService.getUnresponded({ source: selectedSourceTab })
      .then(res => {
        setLeaveRequests(res)
        setDataFetchStatus({ ...constants.fetchStatus.default, isSuccess: true })
      })
      .catch(error => {
        console.log(error)
        setDataFetchStatus({ ...constants.fetchStatus.default, isError: true })
      })
  }

  useEffect(() => {
    fetchLeaveRequestsUnrespondedData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSourceTab])

  function onEditClick(isResponded: boolean, row: LeaveRequest) {
    // TODO: remove comment if edit restriction is needed
    // if (isResponded) toast.error('This document is not editable anymore!')
    // else
    navigate(links.admin.leaveRequestEdit({ id: row.id }))
  }

  function onApproveClick(data: LeaveRequest) {
    if (canApproval)
      adminLeaveRequestApprovalPopupStore.setState({
        approvalStatus: ApprovalStatus.APPROVED,
        data,
        open: true,
      })
    else toast.error("You don't have permission to do this action!")
  }

  function onRejectClick(data: LeaveRequest) {
    if (canApproval)
      adminLeaveRequestApprovalPopupStore.setState({
        approvalStatus: ApprovalStatus.REJECTED,
        data,
        open: true,
      })
    else toast.error("You don't have permission to do this action!")
  }

  function onApprovalPopupConfirm() {
    fetchLeaveRequestsUnrespondedData()
  }

  return {
    sourceTabs,
    selectedSourceTab,
    dataFetchStatus,
    fetchLeaveRequestsUnrespondedData,
    tableData: {
      columns: tableColumns,
      rows: leaveRequests,
    },
    onApproveClick,
    onRejectClick,
    onApprovalPopupConfirm,
  }
}
